<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { computed, onBeforeUnmount, ref } from "vue";
import CrossWhite from "#/assets/iconsDynamic/Close.vue";
import { useThemeStore } from "#/store/theme";

const locatorNumber = ref("");
const MAX_LENGHT = 3;

const emit = defineEmits<{
	(e: "close"): void;
	(e: "create-order", locatorNumber?: string): void;
}>();

const { t } = useTranslation();

const themeStore = useThemeStore();

const isFeedbackActive = ref(false);
let feedbackTimer: ReturnType<typeof setTimeout> | null = null;

const locatorPrefix = computed(() => themeStore.data?.kiosk_locator_prefix);

const triggerFeedback = () => {
	isFeedbackActive.value = false;
	if (feedbackTimer) {
		clearTimeout(feedbackTimer);
	}

	feedbackTimer = setTimeout(() => {
		isFeedbackActive.value = false;
	}, 260);
	isFeedbackActive.value = true;
};

const addLocatorNumber = (number: string) => {
	if (locatorNumber.value.length < MAX_LENGHT) {
		locatorNumber.value += number;
		triggerFeedback();
	}
};

const deleteLocatorNumber = () => {
	if (locatorNumber.value.length > 0) {
		locatorNumber.value = locatorNumber.value.slice(0, -1);
	}
};

const handleCreateOrder = (isNoLocator = false) => {
	const prefix = locatorPrefix.value?.trim() || "";
	const number = isNoLocator ? "" : prefix + locatorNumber.value.trim();
	emit("create-order", number || undefined);
	emit("close");
};

onBeforeUnmount(() => {
	if (feedbackTimer) {
		clearTimeout(feedbackTimer);
	}
});
</script>

<template>
	<div class="locator_overlay" :class="{ locator_feedback_active: isFeedbackActive }" @click.self="emit('close')">
		<div class="locator_card">
			<div class="locator_top_row">
				<button type="button" class="locator_back_button" @click="emit('close')">
					<CrossWhite class="locator_back_svg" />
				</button>
				<div class="locator_circle">
					<div class="locator_ring"></div>
					<div class="locator_ring"></div>
					<div class="locator_ring"></div>
					<p class="locator_number">
						<span v-if="locatorPrefix" class="locator_prefix">{{ locatorPrefix }}</span>
						<span class="locator_number_value">{{ locatorNumber || '' }}</span>
					</p>
				</div>
			</div>
			<p class="locator_title">{{ t(`locator.title`) }}</p>
			<div class="locator_keypad">
				<button type="button" v-for="number in 9" :key="number" class="locator_key" @click="addLocatorNumber(number.toString())">{{ number }}</button>
				<div class="locator_last_row">
					<button type="button" class="locator_key" @click="addLocatorNumber('0')">0</button>
					<button type="button" class="locator_key locator_delete_key" @click="deleteLocatorNumber">⌫</button>
				</div>
			</div>
			<div class="locator_actions">
				<button type="button" class="locator_btn locator_continue_btn" :disabled="locatorNumber.length === 0" @click="handleCreateOrder(false)">
					{{ t(`locator.button.continue`) }}
				</button>
				<button type="button" class="locator_btn no_locator_btn" @click="handleCreateOrder(true)" v-if="!themeStore.data?.kiosk_required_locator">
					{{ t(`locator.button.no-locator`) }}
				</button>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
    @use './locator.scss';
</style>
